import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiDownload, FiX, FiChevronLeft, FiChevronRight, FiArrowUp } from 'react-icons/fi';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px 60px;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 0 15px 40px;
  }
  
  @media (max-width: 480px) {
    padding: 0 10px 30px;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 280px;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 3px;
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #b38b6d;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PhotoCount = styled.p`
  text-align: center;
  color: #947060;
  font-family: 'Lato', sans-serif;
  font-size: 1rem;
  margin-bottom: 30px;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  width: 100%;
  padding: 20px 0;
  justify-items: center;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
    justify-items: center;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    justify-items: center;
    width: 100%;
  }
`;

const PhotoItem = styled(motion.div)`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  background-color: #f5f5f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    
    .download-overlay {
      opacity: 1;
    }
  }
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const DownloadOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DownloadButton = styled.button`
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #947060;
  font-size: 1.2rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: white;
    transform: scale(1.1);
    color: #b38b6d;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Lightbox Styles
const LightboxOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LightboxContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;

const LightboxControls = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 1001;
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
  }
`;

const LightboxButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #947060;
  font-size: 1.2rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: white;
    transform: scale(1.1);
    color: #b38b6d;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const NavigationButton = styled(LightboxButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.position === 'left' ? 'left: 20px;' : 'right: 20px;'}
  
  @media (max-width: 768px) {
    ${props => props.position === 'left' ? 'left: 10px;' : 'right: 10px;'}
  }
`;

const PhotoInfo = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-family: 'Lato', sans-serif;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    bottom: 10px;
    font-size: 0.8rem;
    padding: 6px 12px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: #947060;
  font-family: 'Lato', sans-serif;
  font-size: 1.1rem;
`;

const TabContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 2px solid #e0d5c5;
  margin-bottom: 20px;
  width: 100%;
  justify-content: center;
  flex-wrap: nowrap;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Tab = styled.button.attrs({ type: 'button' })`
  padding: 8px 10px;
  background: ${props => props.active ? 'rgba(179, 139, 109, 0.15)' : 'rgba(255, 255, 255, 0.6)'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#b38b6d' : 'transparent'};
  margin-right: 2px;
  margin-bottom: -2px;
  color: ${props => props.active ? '#b38b6d' : '#666'};
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.85rem;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 1;
  flex-grow: 0;
  
  &:hover {
    background: ${props => props.active ? 'rgba(179, 139, 109, 0.2)' : 'rgba(179, 139, 109, 0.1)'};
    color: #b38b6d;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(179, 139, 109, 0.2);
  }
`;

const MobileCategorySelect = styled.select`
  display: none;
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e0d5c5;
  border-radius: 4px;
  background-color: white;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 20px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23947060' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 12px;
  padding-right: 40px;
  
  &:focus {
    outline: none;
    border-color: #b38b6d;
    box-shadow: 0 0 0 2px rgba(179, 139, 109, 0.2);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const BackToTopButton = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(179, 139, 109, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 100;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(179, 139, 109, 1);
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    bottom: 20px;
    right: 20px;
    font-size: 1.3rem;
  }
`;

const VideoContainer = styled(motion.div)`
  width: 100%;
  max-width: 800px;
  margin: 30px auto 40px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(179, 139, 109, 0.2);
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 20px auto 30px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    border-radius: 6px;
    margin: 15px auto 25px;
    width: 100%;
  }
`;

const StyledVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Photos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const tabsRef = useRef(null);
  
  // Define photo categories with their ranges
  const categories = [
    {
      name: 'Getting Ready',
      ranges: [[20, 194]]
    },
    {
      name: 'First Look',
      ranges: [[217, 291]]
    },
    {
      name: 'Wedding Party + Family',
      ranges: [[292, 490], [945, 966]]
    },
    {
      name: 'Ceremony',
      ranges: [[528, 861]]
    },
    {
      name: 'More Erika and Andrew',
      ranges: [[862, 913], [967, 971]]
    },
    {
      name: 'Guests',
      ranges: [[976, 1084]]
    },
    {
      name: 'Reception',
      ranges: [[1085, 1716]]
    },
    {
      name: 'Big Group Shot',
      ranges: [[1717, 1729]]
    },
    {
      name: 'And More',
      ranges: [] // Will be handled separately as "uncategorized"
    }
  ];
  
  // Extract photo number from filename (e.g., "Wedding photo (123).jpg" -> 123)
  const getPhotoNumber = (filename) => {
    const match = filename.match(/\((\d+)\)/);
    return match ? parseInt(match[1], 10) : null;
  };
  
  // Check if a photo number is in a category's ranges
  const isInCategory = (photoNumber, categoryRanges) => {
    if (categoryRanges.length === 0) return false; // Special case for "And More"
    return categoryRanges.some(([start, end]) => photoNumber >= start && photoNumber <= end);
  };
  
  // Get photos for the active category
  const getCategoryPhotos = (categoryIndex) => {
    if (categoryIndex === 8) {
      // "And More" - all photos not in any other category
      const allCategorizedNumbers = new Set();
      categories.slice(0, 8).forEach(cat => {
        cat.ranges.forEach(([start, end]) => {
          for (let i = start; i <= end; i++) {
            allCategorizedNumbers.add(i);
          }
        });
      });
      
      return photos.filter(photo => {
        const num = getPhotoNumber(photo);
        return num !== null && !allCategorizedNumbers.has(num);
      });
    } else {
      const category = categories[categoryIndex];
      const categoryNumbers = new Set();
      category.ranges.forEach(([start, end]) => {
        for (let i = start; i <= end; i++) {
          categoryNumbers.add(i);
        }
      });
      
      return photos.filter(photo => {
        const num = getPhotoNumber(photo);
        return num !== null && categoryNumbers.has(num);
      });
    }
  };

  useEffect(() => {
    // Load the photo manifest
    fetch('/wedding-photos-manifest.json')
      .then(response => response.json())
      .then(data => {
        setPhotos(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading photos:', error);
        setLoading(false);
      });
  }, []);

  // Show/hide back to top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled down 300px
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTabs = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  const openLightbox = (categoryPhotoIndex) => {
    // Get the category photos and find the absolute index in all photos
    const categoryPhotos = getCategoryPhotos(activeCategory);
    const clickedPhoto = categoryPhotos[categoryPhotoIndex];
    const absoluteIndex = photos.findIndex(p => p === clickedPhoto);
    setCurrentPhotoIndex(absoluteIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const navigatePhoto = (direction) => {
    if (direction === 'next') {
      setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    } else {
      setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };

  const downloadPhoto = async (filename, event) => {
    if (event) {
      event.stopPropagation(); // Prevent lightbox from opening
    }
    
    try {
      // Construct path to high-res version
      const encodedFilename = encodeURIComponent(filename);
      const highResPath = `/img/wedding High Res/${encodedFilename}`;
      
      // Fetch the image
      const response = await fetch(highResPath);
      const blob = await response.blob();
      
      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading photo:', error);
      // Fallback: try direct download
      const encodedFilename = encodeURIComponent(filename);
      const highResPath = `/img/wedding High Res/${encodedFilename}`;
      const link = document.createElement('a');
      link.href = highResPath;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        navigatePhoto('prev');
      } else if (e.key === 'ArrowRight') {
        navigatePhoto('next');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxOpen, currentPhotoIndex, photos.length]);

  const getPhotoPath = (filename) => {
    // URL encode the filename to handle spaces and special characters
    const encodedFilename = encodeURIComponent(filename);
    return `/img/WEDDING PHOTO- websize/${encodedFilename}`;
  };

  const getHighResPath = (filename) => {
    const encodedFilename = encodeURIComponent(filename);
    return `/img/wedding High Res/${encodedFilename}`;
  };

  if (loading) {
    return (
      <PageContainer>
        <PageTitle>
          Photos
          <NameSpan>Erika & Andrew</NameSpan>
        </PageTitle>
        <LoadingSpinner>Loading photos...</LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle>
        Wedding Photos
        <NameSpan>Erika & Andrew</NameSpan>
      </PageTitle>

      <VideoContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <StyledVideo controls>
          <source src="/video/Highlight video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </StyledVideo>
      </VideoContainer>

      {/* Category Tabs */}
      <TabContainer ref={tabsRef}>
        {/* Desktop Tabs */}
        <TabList>
          {categories.map((category, index) => (
            <Tab
              key={index}
              active={activeCategory === index}
              onClick={() => setActiveCategory(index)}
            >
              {category.name}
            </Tab>
          ))}
        </TabList>
        
        {/* Mobile Dropdown */}
        <MobileCategorySelect
          value={activeCategory}
          onChange={(e) => setActiveCategory(parseInt(e.target.value, 10))}
        >
          {categories.map((category, index) => (
            <option key={index} value={index}>
              {category.name}
            </option>
          ))}
        </MobileCategorySelect>
      </TabContainer>

      {/* Gallery for active category */}
      {(() => {
        const categoryPhotos = getCategoryPhotos(activeCategory);
        return (
          <>
            <GalleryGrid>
              {categoryPhotos.map((filename, categoryIndex) => {
                const absoluteIndex = photos.findIndex(p => p === filename);
                return (
                  <PhotoItem
                    key={filename}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: categoryIndex * 0.01 }}
                    onClick={() => openLightbox(categoryIndex)}
                  >
                    <PhotoImage
                      src={getPhotoPath(filename)}
                      alt={`Wedding photo ${absoluteIndex + 1}`}
                      loading="lazy"
                    />
                    <DownloadOverlay className="download-overlay">
                      <DownloadButton
                        onClick={(e) => downloadPhoto(filename, e)}
                        aria-label="Download photo"
                      >
                        <FiDownload />
                      </DownloadButton>
                    </DownloadOverlay>
                  </PhotoItem>
                );
              })}
            </GalleryGrid>
          </>
        );
      })()}

      <AnimatePresence>
        {lightboxOpen && (
          <LightboxOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <LightboxContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <NavigationButton
                position="left"
                onClick={(e) => {
                  e.stopPropagation();
                  navigatePhoto('prev');
                }}
                aria-label="Previous photo"
              >
                <FiChevronLeft />
              </NavigationButton>

              <LightboxImage
                src={getPhotoPath(photos[currentPhotoIndex])}
                alt={`Wedding photo ${currentPhotoIndex + 1}`}
              />

              <NavigationButton
                position="right"
                onClick={(e) => {
                  e.stopPropagation();
                  navigatePhoto('next');
                }}
                aria-label="Next photo"
              >
                <FiChevronRight />
              </NavigationButton>

              <LightboxControls>
                <LightboxButton
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPhoto(photos[currentPhotoIndex], e);
                  }}
                  aria-label="Download photo"
                >
                  <FiDownload />
                </LightboxButton>
                <LightboxButton
                  onClick={(e) => {
                    e.stopPropagation();
                    closeLightbox();
                  }}
                  aria-label="Close lightbox"
                >
                  <FiX />
                </LightboxButton>
              </LightboxControls>

              <PhotoInfo>
                {currentPhotoIndex + 1} / {photos.length}
              </PhotoInfo>
            </LightboxContent>
          </LightboxOverlay>
        )}
      </AnimatePresence>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <BackToTopButton
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTabs}
            aria-label="Back to categories"
          >
            <FiArrowUp />
          </BackToTopButton>
        )}
      </AnimatePresence>
    </PageContainer>
  );
};

export default Photos;
